import { Outlet, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { AlarmBanner } from '../components/AlarmBanner';
import { OfflineBanner } from '../components/OfflineBanner';
import { EmptyState, Spinner } from '../components/ui';
import { type Look, LookProvider, lookOf } from '../design/looks';
import { AgeProvider, ageOf } from '../design/ui';
import { useUnreadCount } from '../pages/NotificationsPage';
import { useSeenSection } from './badges';
import { sectionOf } from './nav';
import { Sidebar, useNavLayout } from './Sidebar';
import { TopBar } from './TopBar';
import { useViewContext } from './ViewContext';

/**
 * The frame of every page (design v4, ref. «Courses»): the bar with the logo, the crumbs, the search
 * and the bell on the page's ground; under it one plate holding the menu and, inside it, the white
 * panel — fixed in height on a computer, only what is inside it scrolls. Inside the panel one margin
 * of 24 px (16 on a phone): the banners and the blocks share the same outer edges; the crumbs and the
 * search above line up with them (docs/design-v4.md). Opening a section marks what was new in it as
 * seen (its count in the menu goes). Pages not redrawn yet keep their grey ground. The look is «Форми 2»
 * (chosen 28.10); the others open on the preview only by `?look=`.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const { signOut, session } = useAuth();
  const { current, loading, contexts } = useViewContext();
  const [open, setOpen] = useState(false);
  const [look] = useState<Look>(lookOf);
  const unread = useUnreadCount();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const layout = useNavLayout();
  useSeenSection(sectionOf(path, layout));
  const grade =
    current?.kind === 'student' || current?.kind === 'guardian'
      ? current.child.class?.grade
      : undefined;
  // read on every render: `?age=` may come with any link of the demo
  const age = grade === undefined ? 'teen' : ageOf(grade);
  // redrawn in v4 so far: the student's home and «Шкільне життя»
  const v4 = (path === '/' && current?.kind === 'student') || path === '/life';
  // «Скло»: the student's home stands straight on the ground, without the plate and the white panel.
  // «Форми 2» (chosen 28.10, «распространять по всей системе»): every page so — its white blocks right on
  // the paper, the page scrolling as any page
  const bare =
    look === 'shapes2' ||
    (current?.kind === 'student' &&
      (look === 'glass' || look === 'glass2' || look === 'shapes') &&
      path === '/');
  // «Скло 2»: the page scrolls as any page (the browser's own bar on the right edge), the bar and the
  // menu stay; its home draws the alarm itself, among its blocks of the moment
  const scrolls = bare && (look === 'glass2' || look === 'shapes' || look === 'shapes2');

  const sidebar = (logo: boolean) => (
    <Sidebar
      ctx={current}
      email={session?.user.email}
      logo={logo}
      onNavigate={() => setOpen(false)}
      onSignOut={() => void signOut()}
    />
  );

  const content = loading ? (
    <Spinner label={t('common.loading')} />
  ) : contexts.length === 0 ? (
    <div className="text-center">
      <EmptyState>{t('context.none')}</EmptyState>
      <button type="button" className="text-sm text-ink underline" onClick={() => void signOut()}>
        {t('common.signOut')}
      </button>
    </div>
  ) : (
    <Outlet />
  );

  return (
    <AgeProvider value={age}>
      <LookProvider value={look}>
        <div
          className={`app-backdrop flex min-h-full flex-col ${scrolls ? 'min-h-screen' : 'md:h-screen md:overflow-hidden'}`}
          data-age={age}
          data-look={look}
          data-bare={bare || undefined}
          data-scrolls={scrolls || undefined}
        >
          <TopBar unread={unread} onMenu={() => setOpen(true)} />
          {open && (
            <div className="fixed inset-0 z-40 md:hidden" onClick={() => setOpen(false)}>
              <div className="absolute inset-0 bg-[rgb(23_23_27/0.24)]" />
              <aside
                className="app-backdrop absolute inset-y-0 left-0 w-[280px]"
                data-look={look}
                onClick={(e) => e.stopPropagation()}
              >
                {sidebar(true)}
              </aside>
            </div>
          )}
          <div className="flex min-h-0 flex-1 md:px-3 md:pb-3">
            <div className="app-plate flex min-h-0 min-w-0 flex-1 md:rounded-[32px] md:p-2">
              <aside className="app-aside hidden w-[232px] shrink-0 md:block">
                {sidebar(false)}
              </aside>
              <div className="app-panel flex min-h-0 min-w-0 flex-1 flex-col rounded-t-[24px] bg-white md:overflow-hidden md:rounded-[24px]">
                <div className="flex flex-col gap-2 px-4 pt-4 empty:hidden md:px-6 md:pt-6 [&>*]:overflow-hidden [&>*]:rounded-2xl">
                  <OfflineBanner />
                  {current && !(scrolls && path === '/') && <AlarmBanner ctx={current} />}
                </div>
                <div className="panel-scroll flex-1 md:overflow-y-auto">
                  <main className="p-4 md:p-6">
                    {v4 || bare ? (
                      content
                    ) : (
                      <div
                        className={`rounded-[28px] bg-canvas ${current?.kind === 'student' ? 'p-2' : 'p-4 md:p-8'}`}
                      >
                        <div className={current?.kind === 'student' ? '' : 'mx-auto max-w-6xl'}>
                          {content}
                        </div>
                      </div>
                    )}
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LookProvider>
    </AgeProvider>
  );
}
